import { TeamsFx } from '@microsoft/teamsfx';
import axios, { AxiosInstance } from 'axios';
import { DoorOpenVerification, CallGroup, CallGroupParticipantVerification, Device, DeviceStatus } from './types';
import { config } from '../../config'

const getCyberGateApiClient = async (): Promise<AxiosInstance> => {

    const teamsfx = new TeamsFx(undefined, {
        "initiateLoginEndpoint": config.startLoginPageUrl,
        "clientId": config.clientID,
    });
   
    const accessToken = await teamsfx.getCredential().getToken("");
    if (!accessToken) {
        throw Error("An unexpected error occurred. Could not retrieve an access token to communicate with CyberGate API.");
    }

    const endpoint = config.cyberGateManagementApi;
    const cyberGateApiClient = axios.create({
        baseURL: endpoint,
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': "Bearer " + accessToken.token,
        }
    });

    cyberGateApiClient.interceptors.response.use((response) => {
        return response;
    },
        (error) => {
            const errorMessage = `Error calling the CyberGate API. Status Code: ${error.status}. Error message: ${error.message}.`;
            console.error(errorMessage);
            return Promise.reject(error);
        });

    return cyberGateApiClient;
}

export function verifyDoorOpen(userPrincipalName: string, chatId: string): Promise<DoorOpenVerification> {
    if (config.mockCyberGateManagementApi === true) {
           return new Promise<DoorOpenVerification>((resolve) => {
            setTimeout(() => {
                let data = require("./dooropenverification.json");
                resolve(data);
            }, 500);
        })
    }

    return new Promise<DoorOpenVerification> (
        (resolve, reject) => {
            const data = {
                "userPrincipalName": userPrincipalName,
                "chatId": chatId
            };

            getCyberGateApiClient().then(client => {
                client.post('api/tenant/app/call/dooropen/verify', data)
                    .then((response) => {
                        resolve(response.data)
                    })
                    .catch((error) => {
                        reject(error)
                    });
            })
        });
}

export function executeDoorOpen(userPrincipalName: string, chatId: string): Promise<void> {
    if (config.mockCyberGateManagementApi === true) {
        return new Promise<void>((resolve, reject) => {
            setTimeout(() => {
                resolve();
            }, 1000);
        })
    }

    return new Promise<void>((resolve, reject) => {
        getCyberGateApiClient().then(client => {
            client.post('api/tenant/app/call/dooropen', {
                userPrincipalName,
                chatId,
            })
                .then((response) => {
                    resolve()
                })
                .catch((error) => {
                    reject(error)
                });
        })
    })
}

export function getCallGroupsCurrentUser(): Promise<CallGroup[]> {
    if (config.mockCyberGateManagementApi === true) {
        console.warn("Mocking CyberGate API.")
        return new Promise<CallGroup[]>(
            (resolve) => {
                setTimeout(() => {
                    let data = require("./callgroups.json");
                    resolve(data);
                }, 3000);
            });
    }

    return new Promise<CallGroup[]>(
        (resolve, reject) => {
            getCyberGateApiClient().then((client) => {
                client.get('api/tenant/app/callGroup')
                    .then((response) => {
                        resolve(response.data)
                    })
                    .catch((error) => {
                        reject(error);
                    })
            })
        });
}

export function setAvailabilityCurrentUser(callGroupId: string, participantId: string, available: boolean): Promise<void> {
    if (config.mockCyberGateManagementApi === true) {
        console.warn("Mocking CyberGate API.")
        return new Promise<void>(
            (resolve) => {
                setTimeout(() => {
                    resolve();
                }, 3000);
            });
    }

    return new Promise<void>(
        (resolve, reject) => {
            const data = {
                "available": available
            };

            getCyberGateApiClient().then((client) => {
                client.patch(`api/tenant/app/callGroup/${callGroupId}/participant/${participantId}/availability`, data)
                    .then((response) => {
                        resolve(response.data.isOrganizationAuthorized)
                    })
                    .catch((error) => {
                        reject(error);
                    })
            })
        });
}


export function verifyCallGroupParticipant(callGroupId: string, teamsUserName: string): Promise<CallGroupParticipantVerification> {
    if (config.mockCyberGateManagementApi === true) {
        console.warn("Mocking CyberGate API.")
        return new Promise<CallGroupParticipantVerification>(
            (resolve) => {
                setTimeout(() => {
                    let data = require("./callgroupparticipantverification.json");
                    resolve(data);
                }, 3000);
            });
    }

    return new Promise<CallGroupParticipantVerification>(
        (resolve, reject) => {
            const data = {
                "teamsUserName": teamsUserName,
            };

            getCyberGateApiClient().then((client) => {
                client.post(`api/tenant/app/callGroup/${callGroupId}/Participant/Verify`, data)
                    .then((response) => {
                        resolve(response.data)
                    })
                    .catch((error) => {
                        reject(error);
                    })
            })
        });
}

export function addCallGroupParticipant(callGroupId: string, teamsUserName: string): Promise<void> {
    if (config.mockCyberGateManagementApi === true) {
        console.warn("Mocking CyberGate API.")
        return new Promise<void>(
            (resolve) => {
                setTimeout(() => {
                    resolve();
                }, 3000);
            });
    }

    return new Promise<void>(
        (resolve, reject) => {
            const data = {
                "teamsUserName": teamsUserName,
            };

            getCyberGateApiClient().then((client) => {
                client.post(`api/tenant/app/callGroup/${callGroupId}/participant`, data)
                    .then((response) => {
                        resolve(response.data.isOrganizationAuthorized)
                    })
                    .catch((error) => {
                        reject(error);
                    })
            })
        });
}

export function deleteCallGroupParticipant(callGroupId: string, participantId: string): Promise<void> {
    if (config.mockCyberGateManagementApi === true) {
        console.warn("Mocking CyberGate API.")
        return new Promise<void>(
            (resolve) => {
                setTimeout(() => {
                    resolve();
                }, 3000);
            });
    }

    return new Promise<void>(
        (resolve, reject) => {
            getCyberGateApiClient().then((client) => {
                client.delete(`api/tenant/app/callGroup/${callGroupId}/participant/${participantId}`)
                    .then((response) => {
                        resolve(response.data.isOrganizationAuthorized)
                    })
                    .catch((error) => {
                        reject(error);
                    })
            })
        });
}

export function isTenantRegistered(): Promise<boolean> {
    if (config.mockCyberGateManagementApi === true) {
        console.warn("Mocking CyberGate API.")
        return new Promise<boolean>(
            (resolve) => {
                setTimeout(() => {
                    resolve(true);
                }, 3000);
            });
    }

    return new Promise<boolean>(
        (resolve, reject) => {
            getCyberGateApiClient().then((client) => {
                client.get('api/tenant/app/authorize')
                    .then((response) => {
                        resolve(response.data.isOrganizationAuthorized)
                    })
                    .catch((error) => {
                        reject(error);
                    })
            })
        });
}

export function getDevices(): Promise<Device[]> {
    if (config.mockCyberGateManagementApi === true) {
        console.warn("Mocking CyberGate API.")
        return new Promise<Device[]>(
            (resolve) => {
                setTimeout(() => {
                    let data = require("./devices.json");
                    resolve(data);
                }, 3000);
            });
    }

    return new Promise<Device[]>(
        (resolve, reject) => {
            getCyberGateApiClient().then((client) => {
                client.get('api/tenant/app/device')
                    .then((response) => {
                        resolve(response.data)
                    })
                    .catch((error) => {
                        reject(error);
                    })
            })
        });
}

export function getDevicesStatus(): Promise<DeviceStatus[]> {
    if (config.mockCyberGateManagementApi === true) {
        console.warn("Mocking CyberGate API.")
        return new Promise<DeviceStatus[]>(
            (resolve) => {
                setTimeout(() => {
                    let data = require("./devicesstatus.json");
                    resolve(data);
                }, 3000);
            });
    }

    return new Promise<DeviceStatus[]>(
        (resolve, reject) => {
            getCyberGateApiClient().then((client) => {
                client.get('api/tenant/app/device/status')
                    .then((response) => {
                        resolve(response.data)
                    })
                    .catch((error) => {
                        reject(error);
                    })
            })
        });
}